/**
 * @fileoverview Orders SWR Hooks
 * 
 * Provides data fetching and mutation hooks for order management.
 * Includes optimistic updates and WebSocket integration.
 * 
 * @module hooks/swr/useOrders
 */

import useSWR, { useSWRConfig, mutate as globalSwrMutate } from 'swr';
import { useRouter } from '@/lib/navigation';
import { useCallback, useEffect } from 'react';
import { 
  getOrder, 
  getOrderById,
  updateOrderMenuStatus as apiUpdateOrderMenuStatus,
} from '@/api';
import { SWR_KEYS, CACHE_TIME } from './swrConfig';
import { toast } from '@/utils/toast';
import { useAdminOrderStore } from '@/stores/useAdminOrderStore';

const EMPTY_ORDERS = [];

// ============================================================================
// FETCHERS
// ============================================================================

/**
 * Fetcher for orders list
 */
const ordersFetcher = async ({ router, startDate, endDate, sector, businessSessionIds }) => {
  const response = await getOrder(router, startDate, endDate, sector, businessSessionIds);
  return response?.data || [];
};

// ============================================================================
// GLOBAL MUTATE REGISTRY
// ============================================================================

/**
 * Global mutate registry for triggering revalidation from non-React code
 * Used by Zustand WebSocket handlers to trigger SWR revalidation
 */

/** Whether an orders list (SWR key [ORDERS, startDate, endDate, sector, sessionIds]) may
 * contain this order: no session filter, or the order's business session is in it. */
const listCanHold = (key, order) => {
  const sessions = Array.isArray(key) ? key[4] : null;
  if (sessions == null || sessions === '') return true;
  if (order?.business_session_id == null) return false;
  return String(sessions).split(',').includes(String(order.business_session_id));
};

export const swrMutate = {
  /**
   * Revalidate all orders (main list)
   */
  orders: () => globalSwrMutate(
    (key) => Array.isArray(key) && key[0] === SWR_KEYS.ORDERS,
    undefined,
    { revalidate: true }
  ),
  
  /**
   * Add a single order to the cache (for new orders via WebSocket)
   */
  addOrder: (newOrder) => globalSwrMutate(
    (key) => Array.isArray(key) && key[0] === SWR_KEYS.ORDERS,
    (currentData) => {
      if (!currentData || !newOrder) return currentData;
      // Check if order already exists to avoid duplicates
      const exists = currentData.some(order => order.id === newOrder.id);
      if (exists) {
        // Update existing order
        return currentData.map(order => order.id === newOrder.id ? newOrder : order);
      }
      // Add new order at the beginning
      return [newOrder, ...currentData];
    },
    // Revalidate too: a mutate that lands while the list request is in flight makes SWR
    // drop that response (race guard). Right after a POS order the Orders page was
    // first loading its list, so it stayed EMPTY (or stale) until a reload.
    { revalidate: true }
  ),
  
  /**
   * Update a single order in the cache
   */
  updateOrder: (updatedOrder) => {
    if (!updatedOrder) return Promise.resolve();
    const isOrders = (key) => Array.isArray(key) && key[0] === SWR_KEYS.ORDERS;
    const replace = (currentData) => currentData?.map(order => order.id === updatedOrder.id ? updatedOrder : order);
    return Promise.all([
      // Lists that can hold it: replace, or add it when it is not there yet (e.g. the
      // CANCELLED order split off by an edit, announced by websocket).
      globalSwrMutate(
        (key) => isOrders(key) && listCanHold(key, updatedOrder),
        (currentData) => {
          if (!currentData) return currentData;
          return currentData.some((order) => order.id === updatedOrder.id)
            ? replace(currentData)
            : [updatedOrder, ...currentData];
        },
        { revalidate: false }
      ),
      // Other session filters: only replace an order they already show.
      globalSwrMutate(
        (key) => isOrders(key) && !listCanHold(key, updatedOrder),
        (currentData) => (currentData ? replace(currentData) : currentData),
        { revalidate: false }
      ),
    ]);
  },
  
  /**
   * Revalidate paid orders
   */
  paidOrders: () => globalSwrMutate(
    (key) => Array.isArray(key) && key[0] === SWR_KEYS.ORDERS_PAID,
    undefined,
    { revalidate: true }
  ),
  
  /**
   * Revalidate a specific order
   */
  order: (id) => globalSwrMutate(SWR_KEYS.ORDER_DETAIL(id)),
  
  /**
   * Revalidate all order-related caches
   */
  allOrders: () => globalSwrMutate(
    (key) => Array.isArray(key) && key[0]?.startsWith('order'),
    undefined,
    { revalidate: true }
  ),
};

/**
 * Calculate pending orders count from orders list
 */
const calculatePendingCount = (orders) => {
  if (!orders || !Array.isArray(orders)) return 0;
  const filteredOrdersNotPaid = orders.filter((order) => order.status === 0);
  return filteredOrdersNotPaid.filter((order) =>
    order.order_menus.some((menu) => menu.status === 0)
  ).length;
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook for fetching orders list
 * 
 * @param {Object} options - Hook options
 * @param {string} options.startDate - Start date filter
 * @param {string} options.endDate - End date filter
 * @param {string} options.sector - Sector filter
 * @param {boolean} options.enabled - Whether to fetch (default: true)
 * @returns {Object} SWR response with orders data
 * 
 * @example
 * const { orders, isLoading, error, mutate, refetch } = useOrders();
 */
export function useOrders({ startDate, endDate, sector, businessSessionIds = null, enabled = true } = {}) {
  const router = useRouter();
  const { mutate: globalMutate } = useSWRConfig();

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    enabled ? [SWR_KEYS.ORDERS, startDate, endDate, sector, businessSessionIds] : null,
    () => ordersFetcher({ router, startDate, endDate, sector, businessSessionIds }),
    {
      revalidateOnFocus: false,
      dedupingInterval: CACHE_TIME.SHORT,
    }
  );

  // a STABLE empty list while loading or on error: a fresh [] on every render made the
  // orders page copy it into state, re-render, and refetch in a loop (1 000+ requests)
  const orders = data || EMPTY_ORDERS;
  const pendingCount = calculatePendingCount(orders);

  return {
    orders,
    pendingCount,
    isLoading,
    isValidating,
    error,
    mutate,
    refetch: () => mutate(),
    // Helper to invalidate all order-related caches
    invalidateAll: () => {
      globalMutate((key) => Array.isArray(key) && key[0]?.startsWith('order'), undefined, { revalidate: true });
    },
  };
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook for order mutations (line status updates)
 * 
 * @returns {Object} Mutation functions
 * 
 * @example
 * const { updateMenuStatus } = useOrderMutations();
 * await updateMenuStatus(orderId, { status: 1 });
 */
export function useOrderMutations() {
  const router = useRouter();
  const { mutate: globalMutate } = useSWRConfig();

  /**
   * Update menu item status within an order
   */
  const updateMenuStatus = useCallback(async (orderId, data, options = {}) => {
    const { onSuccess, onError } = options;

    // Mark this order as locally updated to skip WebSocket-triggered fetch
    useAdminOrderStore.getState().markLocalUpdate(orderId);

    try {
      const response = await apiUpdateOrderMenuStatus(router, orderId, data);
      
      // Fetch only the updated order and merge into cache
      const updatedOrder = await getOrderById(router, orderId);
      await globalMutate(
        (key) => Array.isArray(key) && key[0] === SWR_KEYS.ORDERS,
        (currentData) => currentData?.map(order => order.id === orderId ? updatedOrder : order),
        { revalidate: false }
      );
      
      onSuccess?.(response);
      return response;
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update menu status');
      onError?.(error);
      throw error;
    }
  }, [router, globalMutate]);

  return {
    updateMenuStatus,
  };
}

export default useOrders;
